import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getJumpAnchorScrollTarget,
  type JumpPillBounds,
} from '~/helpers/get-jump-anchor-scroll-target'
import {
  FINAL_REWARDS_FOOTER_HEIGHT_PX,
  getJumpPillRootMargin,
} from '~/helpers/get-jump-pill-root-margin'
import { shouldShowJumpAnchor } from '~/helpers/should-show-jump-anchor'

/**
 * IntersectionObserver reports fractional ratios for elements with
 * subpixel sizes, so "fully visible" is a hair under 1.
 */
const FULLY_VISIBLE_RATIO = 0.99

/**
 * Viewport-driven state for the floating Jump anchor: observes each
 * purchasable jump point pill with an IntersectionObserver and derives
 * whether the anchor shows, plus the scroll-to-the-pills tap behavior.
 *
 * `isFooterShowing` is whether the fixed final-rewards footer is on
 * screen — while it is, a pill behind it counts as not visible.
 */
export function useJumpAnchor(
  purchasableScores: number[],
  isFooterShowing: boolean,
) {
  const [isFullyVisibleByScore, setIsFullyVisibleByScore] = useState<
    ReadonlyMap<number, boolean>
  >(new Map())
  const pillsRef = useRef(
    new Map<number, { element: HTMLElement; observer: IntersectionObserver }>(),
  )
  const isFooterShowingRef = useRef(isFooterShowing)

  const startObserving = useCallback(
    (requiredScore: number, element: HTMLElement) => {
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries.at(-1)
          if (!entry) return
          const isFullyVisible = entry.intersectionRatio >= FULLY_VISIBLE_RATIO
          setIsFullyVisibleByScore((previous) => {
            if (previous.get(requiredScore) === isFullyVisible) return previous
            const next = new Map(previous)
            next.set(requiredScore, isFullyVisible)
            return next
          })
        },
        {
          rootMargin: getJumpPillRootMargin(isFooterShowingRef.current),
          threshold: FULLY_VISIBLE_RATIO,
        },
      )
      observer.observe(element)
      pillsRef.current.set(requiredScore, { element, observer })
    },
    [],
  )

  // Ref callback for a pill button. Re-registering the same element (render
  // churn detaches and reattaches refs) is a no-op, and unmounting keeps the
  // last measurement in state: shouldShowJumpAnchor only consults scores
  // still purchasable, so a stale entry never leaks into the result while a
  // remount just overwrites it — either way the anchor never flickers.
  const observePill = useCallback(
    (requiredScore: number, node: HTMLElement | null) => {
      const pills = pillsRef.current
      const existing = pills.get(requiredScore)
      if (existing?.element === node) return
      if (existing) {
        existing.observer.disconnect()
        pills.delete(requiredScore)
      }
      if (!node) return
      startObserving(requiredScore, node)
    },
    [startObserving],
  )

  // The observed band depends on the footer's state, and an
  // IntersectionObserver's options are immutable — when the footer shows
  // or hides, recreate every observer so each pill re-measures against the
  // new band immediately.
  useEffect(() => {
    if (isFooterShowingRef.current === isFooterShowing) return
    isFooterShowingRef.current = isFooterShowing
    // Iterating while startObserving overwrites entries is safe: it only
    // sets keys that already exist, never adds or removes any.
    for (const [requiredScore, { element, observer }] of pillsRef.current) {
      observer.disconnect()
      startObserving(requiredScore, element)
    }
  }, [isFooterShowing, startObserving])

  const scrollToJumpPoints = useCallback(() => {
    const pills: JumpPillBounds[] = [...pillsRef.current].map(
      ([requiredScore, { element }]) => {
        const rect = element.getBoundingClientRect()
        return {
          requiredScore,
          top: rect.top + window.scrollY,
          bottom: rect.bottom + window.scrollY,
        }
      },
    )
    // The destination band always excludes the footer strip: scrolling
    // away from the page bottom re-shows the footer, so pills must land
    // above it.
    const target = getJumpAnchorScrollTarget(pills, {
      bandTop: 0,
      bandBottom: window.innerHeight - FINAL_REWARDS_FOOTER_HEIGHT_PX,
      viewportHeight: window.innerHeight,
      scrollHeight: document.documentElement.scrollHeight,
    })
    if (target !== null) window.scrollTo({ top: target, behavior: 'smooth' })
  }, [])

  return {
    isVisible: shouldShowJumpAnchor(purchasableScores, isFullyVisibleByScore),
    observePill,
    scrollToJumpPoints,
  }
}
